import { ArrowDown, ArrowRight, ArrowUp, Plus, Minus, Equal } from "lucide-react"
import { formatMonthKey, formatMonthShort } from "@/lib/period-format"

// KPI/KOI scorecard tile. Renders a trailing 12-month ± dot strip fed by
// MonthlyCheckIn.kpi_results / koi_results, plus (when currentMonth is
// provided) an inline control strip that lets the member log +/=/- for
// the current month directly on the dashboard — no separate monthly
// check-in page required.
//
// Props:
//   items: [{ id, name, target, unit, series: [{ monthKey, status }] }]
//     status ∈ "plus" | "equal" | "minus" | null (missing month)
//   heading: "KPIs" | "KOIs"
//   emptyMessage: string shown when items is empty
//   kind (optional): "kpi" | "koi" — which side of the dashboard this is.
//     When absent, the tile is read-only (used on the coach MemberShow).
//   currentMonthResults (optional): [{id, kpi_id|koi_id, name, status}]
//     — the raw rows from this month's MonthlyCheckIn. Matched to `items`
//     by kpi_id/koi_id first, then name. The matched row's id is what
//     the PATCH endpoint expects.
//   onUpdate (optional): async (kind, rowId, value) => boolean
//     Called when the member taps +/=/-. Receives the row id (not the
//     annual KPI id) so the server can address the correct result row.

const STATUS_DOT = {
  plus: "bg-green-500",
  equal: "bg-zinc-300",
  minus: "bg-red-500",
}

function summarize(series) {
  // Look at the last 6 months only for the summary arrow. Simple majority:
  // more "better" months than "worse" trends up (and vice-versa), so logging
  // a single "better" on a fresh KPI immediately reads as trending up rather
  // than sitting on "flat" until a 2-month margin builds.
  const recent = series.slice(-6).filter((s) => s.status !== null)
  if (recent.length === 0) return "flat"
  const plus = recent.filter((s) => s.status === "plus").length
  const minus = recent.filter((s) => s.status === "minus").length
  if (plus > minus) return "up"
  if (minus > plus) return "down"
  return "flat"
}

function SummaryArrow({ trend }) {
  if (trend === "up")
    return (
      <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
        <ArrowUp className="w-3.5 h-3.5" /> trending up
      </span>
    )
  if (trend === "down")
    return (
      <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold">
        <ArrowDown className="w-3.5 h-3.5" /> trending down
      </span>
    )
  return (
    <span className="inline-flex items-center gap-1 text-zinc-500 text-xs font-semibold">
      <ArrowRight className="w-3.5 h-3.5" /> flat
    </span>
  )
}

// Finds the current-month result row for a given KPI/KOI definition.
// Prefers kpi_id/koi_id match (stable across name edits); falls back
// to name match for legacy rows that pre-date the id-carry seed.
function findResultRow(item, rows, idField) {
  if (!Array.isArray(rows)) return null
  return (
    rows.find((r) => r?.[idField] && String(r[idField]) === String(item.id)) ||
    rows.find((r) => r?.name === item.name) ||
    null
  )
}

function StatusButton({ value, active, onClick, disabled, label }) {
  const base =
    "inline-flex items-center justify-center rounded-md h-7 w-8 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  const palette = {
    plus: active
      ? "bg-green-500 text-white"
      : "bg-white border border-zinc-200 text-zinc-500 hover:border-green-400 hover:text-green-600",
    equal: active
      ? "bg-zinc-600 text-white"
      : "bg-white border border-zinc-200 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700",
    minus: active
      ? "bg-red-500 text-white"
      : "bg-white border border-zinc-200 text-zinc-500 hover:border-red-400 hover:text-red-600",
  }
  const Icon = value === "plus" ? Plus : value === "minus" ? Minus : Equal
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      className={`${base} ${palette[value]}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  )
}

function ScorecardTile({ item, kind, currentMonthResults, onUpdate }) {
  const idField = kind === "koi" ? "koi_id" : "kpi_id"
  const resultRow = onUpdate
    ? findResultRow(item, currentMonthResults, idField)
    : null
  const currentStatus = resultRow?.status || null

  // Overlay the live (optimistic) current-month status onto the server-
  // rendered series so the rightmost dot AND the trend arrow reflect
  // the button tap immediately — without waiting for a full Inertia
  // reload. The hook already drives `currentMonthResults`; the series
  // array from props is static across renders.
  const displayedSeries =
    resultRow && item.series.length > 0
      ? [
          ...item.series.slice(0, -1),
          { ...item.series[item.series.length - 1], status: currentStatus },
        ]
      : item.series
  const trend = summarize(displayedSeries)

  const set = (value) => {
    if (!resultRow || !onUpdate) return
    if (currentStatus === value) return
    onUpdate(kind, resultRow.id, value)
  }

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <h4 className="font-display text-base text-zinc-900 truncate">{item.name}</h4>
          {item.target && (
            <p className="text-xs text-zinc-500 mt-0.5">
              Target: {item.target}
              {item.unit ? ` ${item.unit}` : ""}
            </p>
          )}
        </div>
        <SummaryArrow trend={trend} />
      </div>
      <div className="flex gap-1 items-end">
        {displayedSeries.map((s, i) => (
          <div
            key={i}
            className="flex-1 flex flex-col items-center gap-1"
            title={`${formatMonthKey(s.monthKey)}: ${s.status || "no check-in"}`}
          >
            <span
              className={`w-full rounded-full h-2 ${
                s.status ? STATUS_DOT[s.status] : "border border-zinc-200 bg-white"
              }`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-400 mt-1.5">
        <span>{formatMonthShort(displayedSeries[0]?.monthKey)}</span>
        <span>{formatMonthShort(displayedSeries[displayedSeries.length - 1]?.monthKey) || "now"}</span>
      </div>

      {onUpdate && resultRow && (
        <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-zinc-500">
            This month
          </span>
          <div className="flex items-center gap-1">
            <StatusButton
              value="plus"
              active={currentStatus === "plus"}
              onClick={() => set("plus")}
              label="Better this month"
            />
            <StatusButton
              value="equal"
              active={currentStatus === "equal"}
              onClick={() => set("equal")}
              label="Same as last month"
            />
            <StatusButton
              value="minus"
              active={currentStatus === "minus"}
              onClick={() => set("minus")}
              label="Worse this month"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function KpiScorecard({
  items = [],
  heading = "KPIs",
  description,
  emptyMessage,
  kind,
  currentMonthResults,
  onUpdate,
}) {
  const Header = () => (
    <div className="mb-3">
      <h3 className="font-display text-lg text-zinc-900">{heading}</h3>
      {description && (
        <p className="mt-0.5 text-xs text-zinc-500 max-w-2xl">{description}</p>
      )}
    </div>
  )

  if (items.length === 0) {
    return (
      <div>
        <Header />
        <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center">
          <p className="text-sm text-zinc-500">
            {emptyMessage || `No ${heading.toLowerCase()} defined yet.`}
          </p>
        </div>
      </div>
    )
  }
  return (
    <div>
      <Header />
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <ScorecardTile
            key={item.id}
            item={item}
            kind={kind}
            currentMonthResults={currentMonthResults}
            onUpdate={onUpdate}
          />
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-zinc-500 flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> better this month
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-300" /> same
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> worse
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full border border-zinc-300 bg-white" /> no check-in
        </span>
      </div>
    </div>
  )
}
